import { PERMISSIONS, whenProvider } from '@/models/enums/permissions'
import type { INavBarItem } from '@/models/navbar'

const novusNavRoutes = whenProvider('NOVUS', [
  {
    label: 'navigation.management.networkManagement',
    icon: 'network',
    to: '/gerenciamento/rede',
    requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_NETWORK_VIEW,
  },
])

const equatorialNavRoutes = whenProvider('EQUATORIAL', [
  {
    label: 'navigation.parametrization',
    icon: 'cog',
    to: '/parametrizacao',
    requiredPermission: PERMISSIONS.PAGES.PARAMETRIZATION_VIEW,
  },
])

const celescTgNavRoutes = whenProvider('CELESC', [
  {
    label: 'navigation.photocell',
    icon: 'lightbulb',
    to: '/fotocelulas',
    requiredPermission: PERMISSIONS.PAGES.PHOTOC_VIEW,
  },
  {
    label: 'navigation.mdxWater',
    icon: 'droplet',
    to: '/mdx-agua',
    requiredPermission: PERMISSIONS.PAGES.MDX_WATER_VIEW,
  },
])

const celescIagdNavRoutes = whenProvider('CELESC', [
  {
    label: 'navigation.iagd.label',
    icon: 'brain-circuit',
    requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW,
    items: [
      {
        label: 'navigation.iagd.overview',
        icon: 'layout-dashboard',
        to: '/iagd/visao-geral',
        requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW,
      },
      {
        label: 'navigation.iagd.completeness',
        icon: 'table-2',
        to: '/iagd/completude',
        requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW,
      },
      {
        label: 'navigation.iagd.ranking',
        icon: 'chart-bar',
        to: '/iagd/ranking',
        requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW,
      },
      {
        label: 'navigation.iagd.settings',
        icon: 'cog',
        to: '/iagd/configuracoes',
        requiredPermission: PERMISSIONS.PAGES.IAGD_VIEW,
      },
    ],
  },
])

const allRoutes: INavBarItem[] = [
  {
    label: 'navigation.dashboard',
    icon: 'house',
    to: '/dashboard',
    requiredPermission: PERMISSIONS.PAGES.DASHBOARD_VIEW,
  },
  {
    label: 'navigation.meters',
    icon: 'hard-drive',
    to: '/medidores',
    requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
  },
  {
    label: 'navigation.massCommands.label',
    icon: 'mass-command',
    requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
    items: [
      {
        label: 'navigation.massCommands.readings',
        icon: 'reading',
        to: '/comandos-em-massa/leituras',
        requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
      },
      {
        label: 'navigation.massCommands.cutReconnect',
        icon: 'house-plug',
        to: '/comandos-em-massa/corte-religamento',
        requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
      },
    ],
  },
  {
    label: 'navigation.scheduledCommands.label',
    icon: 'calendar-clock',
    to: '/comandos-agendados/listagem-de-rotinas',
    requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
  },

  {
    label: 'navigation.customReport',
    icon: 'file-search',
    to: '/relatorio-customizado',
    requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
  },
  ...novusNavRoutes,
  {
    label: 'navigation.smcs',
    icon: 'server',
    to: '/smcs',
    requiredPermission: PERMISSIONS.PAGES.SMC_VIEW,
  },
  ...celescTgNavRoutes,
  {
    label: 'navigation.energyBalance',
    icon: 'chart-no-axes-combined',
    requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW,
    items: [
      {
        label: 'navigation.transformers',
        icon: 'squares-intersect',
        to: '/balance/transformadores',
        requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW,
      },
      {
        label: 'navigation.areas',
        icon: 'squares-subtract',
        to: '/balance/areas',
        requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW,
      },
      {
        label: 'navigation.regions',
        icon: 'squares-unite',
        to: '/balance/regioes',
        requiredPermission: PERMISSIONS.PAGES.BALANCE_VIEW,
      },
    ],
  },
  {
    label: 'navigation.billing',
    icon: 'billing',
    to: '/faturamento',
    requiredPermission: PERMISSIONS.PAGES.BILLING_VIEW,
  },
  ...equatorialNavRoutes,
  {
    label: 'navigation.dataExtraction',
    icon: 'download',
    to: '/exportacao-de-dados',
    requiredPermission: PERMISSIONS.PAGES.METER_VIEW,
  },
  {
    label: 'navigation.downloadCenter',
    icon: 'download',
    to: '/download-center',
    requiredPermission: PERMISSIONS.PAGES.DOWNLOAD_VIEW,
  },
  {
    label: 'navigation.serviceNotes',
    icon: 'receipt-text',
    to: '/notas-de-servico',
    requiredPermission: PERMISSIONS.PAGES.SERVICE_NOTES_VIEW,
  },
  ...celescIagdNavRoutes,
  {
    label: 'navigation.management.label',
    icon: 'management',
    requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_VIEW,
    items: [
      {
        label: 'navigation.management.models',
        icon: 'layout-dashboard',
        to: '/gerenciamento/modelos',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_MODELS_VIEW,
      },
      {
        label: 'navigation.management.firmware',
        icon: 'file-cog',
        to: '/gerenciamento/firmware',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_FIRMWARE_VIEW,
      },
      {
        label: 'navigation.management.gateways',
        icon: 'network',
        to: '/gerenciamento/gateways',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GATEWAYS_VIEW,
      },
      {
        label: 'navigation.management.users',
        icon: 'user',
        to: '/gerenciamento/usuarios',
        requiredPermission: PERMISSIONS.MANAGE.USER_MANAGE,
      },
      {
        label: 'navigation.management.keys',
        icon: 'key',
        to: '/gerenciamento/chaves',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_KEYS_VIEW,
      },
      {
        label: 'navigation.management.tags',
        icon: 'tag',
        to: '/gerenciamento/tags',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_TAGS_VIEW,
      },
      {
        label: 'navigation.management.groups',
        icon: 'tags',
        to: '/gerenciamento/grupos',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_GROUPS_VIEW,
      },
      {
        label: 'navigation.management.clients',
        icon: 'clients',
        to: '/gerenciamento/clientes',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_CLIENTS_VIEW,
      },
      {
        label: 'navigation.management.configuration',
        icon: 'cog',
        to: '/gerenciamento/configuracoes',
        requiredPermission: PERMISSIONS.PAGES.MANAGEMENT_CONFIGURATION_VIEW,
      },
    ],
  },
]

export const routesNavBar: INavBarItem[] = allRoutes
